import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, onSnapshot, doc, query, where } from 'firebase/firestore';
import SkeletonLoader from '../components/SkeletonLoader';

import LiveMonitor from '../components/OwnerTabs/LiveMonitor';
import CoachDashboard from './CoachDashboard';
import BatchesERP from '../components/OwnerTabs/BatchesERP';
import AcademyHistory from '../components/OwnerTabs/AcademyHistory';

export default function Classroom({ user, setIsClassroomLive }) {
    const [myProfile, setMyProfile] = useState(null);
    const [allClasses, setAllClasses] = useState([]);
    const [allBatches, setAllBatches] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [activeTab, setActiveTab] = useState('');

    useEffect(() => {
        if (!user?.email) return;
        const safeEmail = user.email.toLowerCase();

        const unsubUser = onSnapshot(doc(db, "users", safeEmail), (docSnap) => {
            if (docSnap.exists()) {
                const profile = { id: docSnap.id, ...docSnap.data() };
                setMyProfile(profile);
                if (profile.role === 'STUDENT') setActiveTab('schedule');
                else if (profile.role === 'OWNER') setActiveTab('monitor');
                else setActiveTab('teach');
            }
        });

        return () => unsubUser();
    }, [user]);

    useEffect(() => {
        if (!user?.email || !myProfile) return;
        const safeEmail = user.email.toLowerCase();
        const role = myProfile.role || 'STUDENT';
        const isOwner = role === 'OWNER' || role === 'ADMIN';
        const isCoach = role === 'COACH' || role === 'CHIEF_COACH';

        let unsubClasses, unsubBatches, unsubUsers;

        // 1. Classes subscription
        let qClasses;
        if (isOwner) {
            qClasses = collection(db, "classes");
        } else if (isCoach) {
            qClasses = query(collection(db, "classes"), where("teacherId", "==", safeEmail));
        } else { // Student
            qClasses = query(collection(db, "classes"), where("students", "array-contains", safeEmail));
        }
        unsubClasses = onSnapshot(qClasses, (snap) => {
            const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllClasses(list);
        });

        // 2. Batches subscription (Only Owners need all batches here)
        if (isOwner) {
            unsubBatches = onSnapshot(collection(db, "batches"), (snap) => {
                const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllBatches(list);
            });
        }

        // 3. Users subscription (Owners get all, Coaches get student profiles)
        if (isOwner) {
            unsubUsers = onSnapshot(collection(db, "users"), (snap) => {
                const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllUsers(list);
            });
        } else if (isCoach) {
            const qUsers = query(collection(db, "users"), where("role", "==", "STUDENT"));
            unsubUsers = onSnapshot(qUsers, (snap) => {
                const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setAllUsers(list);
            });
        }

        return () => {
            if (unsubClasses) unsubClasses();
            if (unsubBatches) unsubBatches();
            if (unsubUsers) unsubUsers();
        };
    }, [user, myProfile]);

    const role = myProfile?.role;
    const isOwner = role === 'OWNER' || role === 'ADMIN';
    const isCoach = role === 'COACH' || role === 'CHIEF_COACH';
    const isStudent = role === 'STUDENT';
    
    const safeEmail = user?.email?.toLowerCase();
    const activeCoachLiveClass = allClasses.find(c => c.status === 'LIVE' && c.teacherId === safeEmail);
    const hideHeaderForLiveClass = ((isCoach || isOwner) && activeTab === 'teach' && activeCoachLiveClass);

    useEffect(() => {
        if (setIsClassroomLive) {
            setIsClassroomLive(!!hideHeaderForLiveClass);
        }
        return () => {
            if (setIsClassroomLive) setIsClassroomLive(false);
        };
    }, [hideHeaderForLiveClass, setIsClassroomLive]);

    if (!myProfile || !activeTab) return <SkeletonLoader />;

    const myScheduledClasses = allClasses.filter(c => c.status === 'UPCOMING' && c.students?.includes(safeEmail));
    const myLiveClasses = allClasses.filter(c => c.status === 'LIVE' && c.students?.includes(safeEmail));
    const myPastClasses = allClasses.filter(c => c.status === 'COMPLETED' && c.students?.includes(safeEmail)).sort((a,b) => b.createdAt - a.createdAt);

    return (
         <div style={{
             width: '100%', 
             maxWidth: hideHeaderForLiveClass ? '100%' : '1200px', 
             margin: '0 auto', 
             padding: hideHeaderForLiveClass ? '0' : '20px',
             height: 'auto',
             display: hideHeaderForLiveClass ? 'flex' : 'block',
             flexDirection: 'column',
             flex: hideHeaderForLiveClass ? 1 : 'none',
             minHeight: 0
         }}>
            {!hideHeaderForLiveClass && (
                <>
                    <div style={{display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '30px', padding: '25px' }} className="glass-panel">
                        <div style={{fontSize: '50px', background: 'rgba(59, 130, 246, 0.1)', width: '80px', height: '80px', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '20px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                            🏫
                        </div>
                        <div>
                            <h1 style={{color: 'var(--text-primary)', margin: 0, fontSize: '32px', letterSpacing: '1px' }}>Academy Classroom</h1>
                            <p style={{color: '#10b981', margin: '5px 0 0 0', fontWeight: 'bold', fontSize: '15px' }}>
                                {isOwner ? 'Master Control: Batches, Live Monitor & History' : isCoach ? 'Your Teaching Hub & Schedules' : 'Your Learning Schedule & History'}
                            </p>
                        </div>
                    </div>

                    <style>{`
                        .tab-sub-btn {
                            padding: 8px 18px; border-radius: 14px; font-weight: 700; cursor: pointer; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); font-size: 13px; white-space: nowrap; border: none; background: transparent; color: var(--text-secondary); outline: none; display: inline-flex; align-items: center; gap: 8px; font-family: 'Outfit', sans-serif;
                        }
                        .tab-sub-btn:hover {
                            background: rgba(163, 177, 198, 0.15); color: var(--text-primary); transform: translateY(-1.5px);
                        }
                        .tab-sub-btn-active {
                            padding: 8px 18px; border-radius: 14px; font-weight: 700; cursor: pointer; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); font-size: 13px; white-space: nowrap; border: none; background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; outline: none; boxShadow: 0 6px 20px rgba(59, 130, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.35); display: inline-flex; align-items: center; gap: 8px; font-family: 'Outfit', sans-serif;
                        }
                    `}</style>

                    <div style={{ display: 'flex', gap: '6px', marginBottom: '28px', overflowX: 'auto', padding: '6px', background: 'var(--bg-primary)', borderRadius: '18px', border: '1px solid rgba(163, 177, 198, 0.3)', boxShadow: 'var(--panel-shadow)', alignItems: 'center' }}>
                        {isStudent ? (
                            <>
                                <button 
                                    onClick={() => setActiveTab('schedule')} 
                                    className={activeTab === 'schedule' ? "tab-sub-btn-active" : "tab-sub-btn"}
                                >
                                    🗓️ Quest Schedule
                                </button>
                                <button 
                                    onClick={() => setActiveTab('history')} 
                                    className={activeTab === 'history' ? "tab-sub-btn-active" : "tab-sub-btn"}
                                    style={activeTab === 'history' ? { background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)' } : {}}
                                >
                                    📚 Training Logbook
                                </button>
                            </>
                        ) : (
                            <>
                                {isOwner && <button onClick={() => setActiveTab('monitor')} className={activeTab === 'monitor' ? "tab-sub-btn-active" : "tab-sub-btn"} style={activeTab === 'monitor' ? {background: 'linear-gradient(135deg, #ef4444 0%, #be123c 100%)', boxShadow: '0 6px 20px rgba(239, 68, 68, 0.4)'} : {}}>📡 Live Monitor</button>}
                                <button onClick={() => setActiveTab('teach')} className={activeTab === 'teach' ? "tab-sub-btn-active" : "tab-sub-btn"}>📖 Teach Class</button>
                                {isOwner && <button onClick={() => setActiveTab('batches')} className={activeTab === 'batches' ? "tab-sub-btn-active" : "tab-sub-btn"} style={activeTab === 'batches' ? {background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)'} : {}}>🏫 Manage Batches</button>}
                                <button onClick={() => setActiveTab('history')} className={activeTab === 'history' ? "tab-sub-btn-active" : "tab-sub-btn"} style={activeTab === 'history' ? {background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)'} : {}}>⏱️ Class History</button>
                            </>
                        )}
                    </div>
                </>
            )}

            <div style={{
                width: '100%',
                height: hideHeaderForLiveClass ? '100%' : 'auto',
                display: hideHeaderForLiveClass ? 'flex' : 'block',
                flexDirection: 'column',
                flex: hideHeaderForLiveClass ? 1 : 'none',
                minHeight: 0
            }}>
                {isStudent && activeTab === 'schedule' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        {myLiveClasses.length > 0 && (
                            <div className="glass-panel animate-pulse-ring animate-wiggle" style={{ padding: '25px', border: '3px solid #ef4444', background: 'var(--bg-primary)', borderRadius: '28px' }}>
                                <h3 style={{ color: '#ef4444', margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '24px', fontWeight: '700' }}>
                                    <span style={{ animation: 'pulse 1s infinite' }}>🔴</span> CLASSROOM IS LIVE!
                                </h3>
                                {myLiveClasses.map(c => (
                                    <div key={c.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'space-between', alignItems: 'center', padding: '15px 0' }}>
                                        <div>
                                            <div style={{ color: 'var(--text-primary)', fontWeight: '700', fontSize: '22px' }}>{c.topic || c.batchTitle}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 'bold', marginTop: '4px' }}>Ready to launch your space mission? Tap the button below!</div>
                                        </div>
                                        <button 
                                            onClick={() => {
                                                // Trigger auto-teleport or manual launch in dashboard
                                                window.location.reload(); 
                                            }}
                                            className="kid-btn-bubble"
                                            style={{
                                                padding: '12px 28px',
                                                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                                                color: '#fff',
                                                border: '2px solid #fff',
                                                borderRadius: '50px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                fontSize: '15px',
                                                boxShadow: '0 5px 15px rgba(239, 68, 68, 0.4)'
                                            }}
                                        >
                                            🚀 GO TO CLASSROOM
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '22px', fontWeight: '700' }}>
                                🗓️ Upcoming Space Flights
                            </h3>
                            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>
                                Mark your calendar, Captain! These chess battles are coming up next.
                            </p>
                        </div>

                        {myScheduledClasses.length === 0 ? (
                            <div className="glass-panel kid-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: '600', border: '3px solid rgba(255,255,255,0.95)', background: 'var(--bg-primary)' }}>
                                No upcoming classes scheduled. Rest time! ☕🍕
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {myScheduledClasses.map(c => (
                                    <div key={c.id} className="glass-panel" style={{ padding: '25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '3px solid rgba(255, 255, 255, 0.95)', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)', flexWrap: 'wrap', gap: '15px', borderRadius: '28px' }}>
                                        <div>
                                            <div style={{ color: '#2563eb', fontSize: '14px', fontWeight: '700', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                                📅 {new Date(c.scheduledAt).toLocaleString()}
                                            </div>
                                            <div style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700' }}>
                                                {c.topic || c.batchTitle}
                                            </div>
                                        </div>
                                        <div style={{ 
                                            background: 'var(--bg-primary)', 
                                            boxShadow: 'var(--neu-shadow-inset)', 
                                            padding: '10px 20px', 
                                            borderRadius: '20px', 
                                            color: 'var(--text-primary)', 
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            border: '1.5px solid #fff'
                                        }}>
                                            Coach: <span style={{ color: '#2563eb' }}>{c.teacherId.split('@')[0]}</span> 👨‍🏫
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {isStudent && activeTab === 'history' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <h3 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '22px', fontWeight: '700' }}>
                                📚 Completed Logbook & Notes
                            </h3>
                            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>
                                Revisit what you learned on your past training sessions!
                            </p>
                        </div>

                        {myPastClasses.length === 0 ? (
                            <div className="glass-panel kid-card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontWeight: '800', border: '3px solid rgba(255,255,255,0.95)', background: 'var(--bg-primary)' }}>
                                No completed classes yet. Let's make some history soon! 🏆
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {myPastClasses.map(c => (
                                    <div key={c.id} className="glass-panel" style={{ padding: '25px', border: '3px solid rgba(255, 255, 255, 0.95)', background: 'var(--bg-primary)', boxShadow: 'var(--panel-shadow)', borderRadius: '28px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                                            <div style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '600' }}>
                                                {c.topic || c.batchTitle}
                                            </div>
                                            <span style={{ 
                                                background: 'linear-gradient(135deg, #10b981, #059669)', 
                                                color: '#fff', 
                                                padding: '6px 16px', 
                                                borderRadius: '20px', 
                                                fontSize: '11px', 
                                                fontWeight: '600',
                                                border: '1.5px solid #fff',
                                                boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)'
                                            }}>
                                                ✅ SUCCESSFUL
                                            </span>
                                        </div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '15px', fontWeight: '700' }}>
                                            🛸 Guided by <strong style={{ color: '#2563eb' }}>{c.teacherId.split('@')[0]}</strong> on {new Date(c.createdAt || Date.now()).toLocaleDateString()}
                                        </div>
                                        {c.whiteboardText && (
                                            <div style={{ 
                                                marginTop: '20px', 
                                                background: 'var(--bg-primary)', 
                                                boxShadow: 'var(--neu-shadow-inset)', 
                                                borderRadius: '16px', 
                                                padding: '15px 20px', 
                                                fontSize: '14px', 
                                                lineHeight: '1.6', 
                                                color: 'var(--text-primary)', 
                                                fontWeight: '600',
                                                border: '1px solid rgba(255, 255, 255, 0.5)',
                                                whiteSpace: 'pre-wrap'
                                            }}>
                                                <strong>📝 Coach's Class Notes:</strong><br/>
                                                {c.whiteboardText}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div key={activeTab} className="animate-fade-in" style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {(isCoach || isOwner) && activeTab === 'teach' && (
                         <CoachDashboard user={user} isEmbedded={true} />
                    )}

                    {(isCoach || isOwner) && activeTab === 'history' && (
                        <AcademyHistory allClasses={allClasses} allUsers={allUsers} myEmail={safeEmail} isOwner={isOwner} />
                    )}

                    {isOwner && activeTab === 'monitor' && <LiveMonitor allClasses={allClasses} />}
                    {isOwner && activeTab === 'batches' && <BatchesERP allBatches={allBatches} coachesList={allUsers.filter(u=>u.role==='COACH')} />}
                </div>
            </div>
         </div>
    );
}
